import { $isLinkNode } from "@lexical/link";
import { $findMatchingParent } from "@lexical/utils";
import {
  $getNearestNodeFromDOMNode,
  defineExtension,
  safeCast,
  type LexicalExtension,
} from "lexical";
import { checkUrl, DEFAULT_URL_POLICY, type UrlPolicy } from "../../core/url-policy";

export interface LinkActivateConfig {
  /**
   * The host's handler, as of the latest render. Returning `true` says the host opened the link
   * itself; anything else leaves it to the editor, which opens it in a new tab.
   */
  activate: { current: ((url: string, event: MouseEvent) => boolean | void) | null };
  urlPolicy: UrlPolicy;
}

/**
 * Opens links. In an editable editor a plain click places the caret, as it does in any text, so a
 * link opens on Mod+click (Cmd on Apple platforms, Ctrl elsewhere); in a read-only one, on any
 * click. A URL the policy refuses is never opened, whoever asked.
 */
export const LinkActivateExtension: LexicalExtension<
  LinkActivateConfig,
  "z-editor/link-activate",
  unknown,
  unknown
> = defineExtension({
  name: "z-editor/link-activate",
  config: safeCast<LinkActivateConfig>({
    activate: { current: null },
    urlPolicy: DEFAULT_URL_POLICY,
  }),
  register(editor, config) {
    const onClick = (event: MouseEvent): void => {
      if (event.button !== 0) return;
      if (editor.isEditable() && !(event.metaKey || event.ctrlKey)) return;
      const target = event.target instanceof Element ? event.target.closest("a") : null;
      if (target === null) return;
      const url = editor.read(() => {
        const node = $getNearestNodeFromDOMNode(target);
        const link = node === null ? null : $findMatchingParent(node, $isLinkNode);
        return $isLinkNode(link) ? link.getURL() : null;
      });
      if (url === null || !checkUrl(url, config.urlPolicy).ok) return;
      event.preventDefault();
      if (config.activate.current?.(url, event) === true) return;
      target.ownerDocument.defaultView?.open(url, "_blank", "noopener,noreferrer");
    };
    return editor.registerRootListener((root, previous) => {
      previous?.removeEventListener("click", onClick);
      root?.addEventListener("click", onClick);
    });
  },
});
